import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/environment';
import ProxmoxHostManagement from './ProxmoxHostManagement';
import VPSFirewallManagement from './VPSFirewallManagement';
import ProxmoxHostFirewallManagement from './ProxmoxHostFirewallManagement';
import IPv4PoolManagement from './IPv4PoolManagement';
import VPSBackupManagement from './VPSBackupManagement';
import OSTemplateManagement from './OSTemplateManagement';
import OSCategoryCards from '../vps/OSCategoryCards';
import type { OSTemplate } from '../../services/osTemplateService';

interface VPSInstance {
  id: string;
  customer_id: string;
  email: string;
  first_name: string;
  last_name: string;
  lxd_server_id: string;
  server_name: string;
  container_name: string;
  name: string;
  hostname: string;
  ipv4_address: string | null;
  ipv6_address: string | null;
  os_template: string;
  status: 'creating' | 'running' | 'stopped' | 'suspended' | 'terminated';
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
  bandwidth_gb: number;
  plan_name: string;
  monthly_price: number;
  created_at: string;
  updated_at: string;
  actual_disk_used_gb?: number | null;  // Actual disk used from Proxmox
  actual_disk_max_gb?: number | null;   // Max disk from Proxmox
  live_status?: string | null;          // Real-time status from Proxmox
  proxmox_host_display?: string;        // Host name for display
  proxmox_vmid?: number | null;         // Proxmox VM ID
  proxmox_node?: string | null;         // Proxmox node name
}

interface VPSPlan {
  id: string;
  name: string;
  description: string;
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
  bandwidth_gb: number;
  monthly_price: number;
  hourly_price: number;
  features: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// LXDServer interface removed - system now uses libvirt only

// Helper function to get the default SSH username based on OS template
const getDefaultUsernameForOS = (osTemplate: string): string => {
  if (!osTemplate) return 'ubuntu';

  const os = osTemplate.toLowerCase();

  if (os.includes('debian')) return 'debian';
  if (os.includes('almalinux') || os.includes('alma')) return 'almalinux';
  if (os.includes('rocky')) return 'rocky';
  if (os.includes('fedora')) return 'fedora';
  if (os.includes('centos')) return 'centos';
  if (os.includes('ubuntu')) return 'ubuntu';

  // Default to ubuntu for unknown OS
  return 'ubuntu';
};

const VPSManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'instances' | 'plans' | 'users' | 'cleanup' | 'proxmox-hosts' | 'firewall' | 'host-firewall' | 'ipv4-pool' | 'backups' | 'os-templates'>('instances');
  const [vpsInstances, setVpsInstances] = useState<VPSInstance[]>([]);
  const [vpsPlans, setVpsPlans] = useState<VPSPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreatePlanForm, setShowCreatePlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<VPSPlan | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userVPSOverview, setUserVPSOverview] = useState<any>(null);
  const [searchEmail, setSearchEmail] = useState('');
  const [cleanupHours, setCleanupHours] = useState(1);
  const [newVPSLimit, setNewVPSLimit] = useState(5);
  const [searchingUser, setSearchingUser] = useState(false);
  // New states for enhanced user management
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [cleanupProgress, setCleanupProgress] = useState<any>(null);
  // Image population state removed - libvirt uses cloud images automatically
  
  // Filters and sorting for VPS instances
  // Default to 'running' to show only active VPSs
  const [filterStatus, setFilterStatus] = useState<string>('running');
  const [filterServer, setFilterServer] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedInstances, setSelectedInstances] = useState<string[]>([]);
  
  // States for networking management
  const [showNetworkingModal, setShowNetworkingModal] = useState(false);
  const [selectedInstanceForNetworking, setSelectedInstanceForNetworking] = useState<any>(null);
  const [networkingInfo, setNetworkingInfo] = useState<any>(null);
  const [connectionInfo, setConnectionInfo] = useState<any>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showSSHKeyAdd, setShowSSHKeyAdd] = useState(false);
  const [newSSHKey, setNewSSHKey] = useState('');
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [provisionForm, setProvisionForm] = useState({
    customer_id: '',
    plan_id: '',
    name: '',
    os_template: 'ubuntu:22.04',
    template_id: '' as string, // Dynamic OS template ID from database
    sshUsername: 'ubuntu',
    initialPassword: '',
    sshPublicKeys: [] as string[],
    proxmox_host_id: '' // New field for host selection
  });
  const [selectedOSTemplate, setSelectedOSTemplate] = useState<OSTemplate | null>(null);
  const [provisionSSHKey, setProvisionSSHKey] = useState('');

  // Proxmox hosts for provisioning
  const [proxmoxHosts, setProxmoxHosts] = useState<any[]>([]);
  const [loadingHosts, setLoadingHosts] = useState(false);
  
  // Password display modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [displayedPassword, setDisplayedPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);
  
  // OS Templates (fetched from API)
  const [osTemplates, setOsTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Console/Provisioning Logs states
  const [showConsoleModal, setShowConsoleModal] = useState(false);
  const [selectedInstanceForConsole, setSelectedInstanceForConsole] = useState<any>(null);
  const [provisioningLogs, setProvisioningLogs] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsPollInterval, setLogsPollInterval] = useState<NodeJS.Timeout | null>(null);

  // VPS Operation tracking states
  const [vpsOperations, setVpsOperations] = useState<Map<string, {
    action: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    message: string;
    operationKey?: string;
  }>>(new Map());
  const [refreshingStatuses, setRefreshingStatuses] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);

  // Memory management states
  const [showMemoryModal, setShowMemoryModal] = useState(false);
  const [selectedInstanceForMemory, setSelectedInstanceForMemory] = useState<VPSInstance | null>(null);
  const [newMemoryMB, setNewMemoryMB] = useState(0);
  const [memoryChanging, setMemoryChanging] = useState(false);
  const [memoryUpdateResult, setMemoryUpdateResult] = useState<{success: boolean; message: string; requiresRestart?: boolean} | null>(null);
  const [restartingAfterMemory, setRestartingAfterMemory] = useState(false);

  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    cpu_cores: 1,
    memory_mb: 1024,
    disk_gb: 25,
    bandwidth_gb: 1000,
    monthly_price: 5.00,
    features: {
      ssd_storage: true,
      backup_included: false,
      monitoring_included: true,
      load_balancer: false,
      dedicated_ip: true
    },
    is_active: true
  });


  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'instances') {
      fetchVPSStatistics();
      // Load Proxmox hosts so the storage-summary widget can compute real
      // total capacity (sum of VPS hosts) instead of falling back to a
      // hardcoded number when the provision modal hasn't been opened.
      fetchProxmoxHosts();
    } else if (activeTab === 'users') {
      fetchAllUsers();
    }
  }, [activeTab]);

  // Auto-refresh VPS instances - faster when operations are in progress
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (activeTab === 'instances' && autoRefresh) {
      const hasActiveOps = Array.from(vpsOperations.values()).some(op => op.status === 'in_progress');
      const refreshInterval = hasActiveOps ? 5000 : 15000; // 5s during ops, 15s otherwise
      interval = setInterval(() => {
        fetchVPSInstances();
        setLastRefreshed(new Date());
      }, refreshInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, autoRefresh, vpsOperations]);

  // Load customers, plans, OS templates, and Proxmox hosts when provision modal opens
  useEffect(() => {
    if (showProvisionModal) {
      // Fetch users if not already loaded
      if (allUsers.length === 0) {
        fetchAllUsers();
      }
      // Fetch plans if not already loaded
      if (vpsPlans.length === 0) {
        fetchVPSPlans();
      }
      // Fetch OS templates
      if (osTemplates.length === 0) {
        fetchOSTemplates();
      }
      // Fetch Proxmox hosts - always refresh to get current capacity
      fetchProxmoxHosts();
    }
  }, [showProvisionModal]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'instances') {
        await fetchVPSInstances();
      } else if (activeTab === 'plans') {
        await fetchVPSPlans();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchVPSInstances = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/vps/instances'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setVpsInstances(result.data || []);
      } else {
        setError(result.error || 'Failed to fetch VPS instances');
      }
    } catch (error) {
      console.error('Error fetching VPS instances:', error);
      setError('Failed to fetch VPS instances');
    }
  };

  const fetchVPSPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/vps/plans'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setVpsPlans(result.data || []);
      } else {
        setError(result.error || 'Failed to fetch VPS plans');
      }
    } catch (error) {
      console.error('Error fetching VPS plans:', error);
      setError('Failed to fetch VPS plans');
    }
  };


  // Fetch OS templates from API
  const fetchOSTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiUrl()}/admin/vps/images`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setOsTemplates(data.data);
        }
      } else {
        console.error('Failed to fetch OS templates');
        // Fallback to static list if API fails
        setOsTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching OS templates:', error);
      // Fallback to static list on error
      setOsTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Fetch Proxmox hosts for provisioning
  const fetchProxmoxHosts = async () => {
    try {
      setLoadingHosts(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/proxmox-hosts/active'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        const hosts = result.data || result.hosts || [];
        setProxmoxHosts(hosts);
        // Auto-select if only one host or select the default host
        if (hosts.length === 1) {
          setProvisionForm(prev => ({ ...prev, proxmox_host_id: hosts[0].id.toString() }));
        } else {
          const defaultHost = hosts.find((h: any) => h.is_default);
          if (defaultHost) {
            setProvisionForm(prev => ({ ...prev, proxmox_host_id: defaultHost.id.toString() }));
          }
        }
      } else {
        console.error('Failed to fetch Proxmox hosts:', result.error);
      }
    } catch (error) {
      console.error('Error fetching Proxmox hosts:', error);
    } finally {
      setLoadingHosts(false);
    }
  };

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    // If editingPlan is set, route to update instead of create — the form
    // is the same UI repurposed for both create and edit.
    if (editingPlan) {
      return handleUpdatePlan(e);
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/vps/plans'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...planForm,
          hourly_price: planForm.monthly_price / 30 / 24
        })
      });

      const result = await response.json();
      if (result.success) {
        setShowCreatePlanForm(false);
        resetPlanForm();
        fetchVPSPlans();
      } else {
        setError(result.error || 'Failed to create VPS plan');
      }
    } catch (error) {
      console.error('Error creating VPS plan:', error);
      setError('Failed to create VPS plan');
    }
  };

  // Open the plan form pre-populated with an existing plan's values.
  const handleStartEditPlan = (plan: VPSPlan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name || '',
      description: plan.description || '',
      cpu_cores: plan.cpu_cores ?? 1,
      memory_mb: plan.memory_mb ?? 1024,
      disk_gb: plan.disk_gb ?? 25,
      bandwidth_gb: plan.bandwidth_gb ?? 1000,
      monthly_price: typeof plan.monthly_price === 'number'
        ? plan.monthly_price
        : parseFloat(plan.monthly_price as any) || 5.00,
      features: {
        ssd_storage: plan.features?.ssd_storage ?? true,
        backup_included: plan.features?.backup_included ?? false,
        monitoring_included: plan.features?.monitoring_included ?? true,
        load_balancer: plan.features?.load_balancer ?? false,
        dedicated_ip: plan.features?.dedicated_ip ?? true,
      },
      is_active: plan.is_active ?? true,
    });
    setShowCreatePlanForm(true);
    setError('');
  };

  const handleUpdatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPlan) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/plans/${editingPlan.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...planForm,
          hourly_price: planForm.monthly_price / 30 / 24,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setShowCreatePlanForm(false);
        setEditingPlan(null);
        resetPlanForm();
        fetchVPSPlans();
      } else {
        setError(result.error || 'Failed to update VPS plan');
      }
    } catch (error) {
      console.error('Error updating VPS plan:', error);
      setError('Failed to update VPS plan');
    }
  };

  // Delete a plan. Backend refuses if any active subscription still
  // references it (409 Conflict with activeSubscriptions count).
  const handleDeletePlan = async (plan: VPSPlan) => {
    const ok = window.confirm(
      `Delete the "${plan.name}" plan?\n\nThis only works if no active subscriptions are using it.`,
    );
    if (!ok) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/plans/${plan.id}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.success) {
        fetchVPSPlans();
      } else if (response.status === 409 && result.activeSubscriptions) {
        setError(`Cannot delete "${plan.name}" — ${result.activeSubscriptions} active subscription(s) still reference it. Cancel or migrate them first.`);
      } else {
        setError(result.error || 'Failed to delete VPS plan');
      }
    } catch (error) {
      console.error('Error deleting VPS plan:', error);
      setError('Failed to delete VPS plan');
    }
  };


  const handleControlVPS = async (instanceId: string, action: string) => {
    // Prevent concurrent operations on the same instance
    const existingOp = vpsOperations.get(instanceId);
    if (existingOp?.status === 'in_progress') {
      return;
    }
    try {
      // Set operation as in_progress immediately
      setVpsOperations(prev => {
        const newMap = new Map(prev);
        newMap.set(instanceId, {
          action,
          status: 'in_progress',
          message: `${action.charAt(0).toUpperCase() + action.slice(1)}ing VPS...`
        });
        return newMap;
      });

      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${instanceId}/control`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });

      const result = await response.json();
      if (result.success) {
        // Store the operation key for polling
        const operationKey = result.operationKey;
        setVpsOperations(prev => {
          const newMap = new Map(prev);
          newMap.set(instanceId, {
            action,
            status: 'in_progress',
            message: `${action.charAt(0).toUpperCase() + action.slice(1)}ing VPS...`,
            operationKey
          });
          return newMap;
        });

        // Poll for operation status
        pollOperationStatus(instanceId, operationKey, action);
      } else {
        setVpsOperations(prev => {
          const newMap = new Map(prev);
          newMap.set(instanceId, {
            action,
            status: 'failed',
            message: result.error || `Failed to ${action} VPS`
          });
          return newMap;
        });
        setError(result.error || `Failed to ${action} VPS instance`);
      }
    } catch (error) {
      console.error(`Error ${action} VPS instance:`, error);
      setVpsOperations(prev => {
        const newMap = new Map(prev);
        newMap.set(instanceId, {
          action,
          status: 'failed',
          message: `Failed to ${action} VPS`
        });
        return newMap;
      });
      setError(`Failed to ${action} VPS instance`);
    }
  };

  // Poll operation status until completed or failed
  const pollOperationStatus = async (instanceId: string, operationKey: string, action: string) => {
    const token = localStorage.getItem('token');
    let attempts = 0;
    const maxAttempts = 60; // Max 60 attempts (2 minutes with 2s interval)

    const poll = async () => {
      attempts++;
      try {
        const response = await fetch(getApiUrl(`/admin/vps/operations/${operationKey}/status`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const result = await response.json();
        if (result.success) {
          const opStatus = result.data;
          setVpsOperations(prev => {
            const newMap = new Map(prev);
            newMap.set(instanceId, {
              action,
              status: opStatus.status,
              message: opStatus.message,
              operationKey
            });
            return newMap;
          });

          if (opStatus.status === 'completed') {
            setSuccess(`VPS ${action} completed successfully`);

            // Optimistically update the local state immediately
            const newStatus = action === 'stop' ? 'stopped' : action === 'start' ? 'running' : action === 'restart' ? 'running' : null;
            if (newStatus) {
              setVpsInstances(prev => prev.map(instance =>
                instance.id === instanceId
                  ? { ...instance, status: newStatus, live_status: newStatus }
                  : instance
              ));
            }

            // Also fetch to get the full updated data from server
            fetchVPSInstances();

            // Clear operation after 3 seconds
            setTimeout(() => {
              setVpsOperations(prev => {
                const newMap = new Map(prev);
                newMap.delete(instanceId);
                return newMap;
              });
            }, 3000);
            return;
          } else if (opStatus.status === 'failed') {
            setError(opStatus.message || `VPS ${action} failed`);
            // Clear operation after 5 seconds
            setTimeout(() => {
              setVpsOperations(prev => {
                const newMap = new Map(prev);
                newMap.delete(instanceId);
                return newMap;
              });
            }, 5000);
            return;
          }
        }

        // Continue polling if in_progress and not exceeded max attempts
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          // Timeout - refresh anyway
          fetchVPSInstances();
          setVpsOperations(prev => {
            const newMap = new Map(prev);
            newMap.delete(instanceId);
            return newMap;
          });
        }
      } catch (error) {
        console.error('Error polling operation status:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        }
      }
    };

    // Start polling after 1 second
    setTimeout(poll, 1000);
  };

  // Refresh all VPS statuses from Proxmox
  const refreshAllStatuses = async () => {
    try {
      setRefreshingStatuses(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/vps/refresh-statuses'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setLastRefreshed(new Date());
        if (result.data.updated > 0) {
          setSuccess(`Updated ${result.data.updated} VPS status(es) from Proxmox`);
        }
        fetchVPSInstances(); // Refresh the list
      } else {
        setError(result.error || 'Failed to refresh VPS statuses');
      }
    } catch (error) {
      console.error('Error refreshing VPS statuses:', error);
      setError('Failed to refresh VPS statuses');
    } finally {
      setRefreshingStatuses(false);
    }
  };


  const fetchVPSStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/vps/statistics'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error fetching VPS statistics:', error);
    }
  };

  const fetchAllUsers = async (search: string = '') => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await fetch(getApiUrl(`/admin/vps/users${searchParam}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAllUsers(result.data);
          setFilteredUsers(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
    
    if (term.trim() === '') {
      setFilteredUsers(allUsers);
    } else {
      // Real-time local filtering for immediate response
      const filtered = allUsers.filter(user => 
        user.email.toLowerCase().includes(term.toLowerCase()) ||
        user.first_name.toLowerCase().includes(term.toLowerCase()) ||
        user.last_name.toLowerCase().includes(term.toLowerCase()) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredUsers(filtered);
      
      // Also fetch from server for more comprehensive search
      if (term.length > 2) {
        fetchAllUsers(term);
      }
    }
  };

  const selectUser = async (user: any) => {
    setSelectedUserId(user.id);
    setNewVPSLimit(user.max_vps_allowed || 5);
    setShowUserDetails(true);
    await fetchUserVPSOverview(user.id);
  };

  const searchUserByEmail = async () => {
    if (!searchEmail) {
      setError('Please enter an email address');
      return;
    }

    console.log('🔍 Searching for user:', searchEmail);
    setSearchingUser(true);
    setError(''); // Clear any previous errors
    
    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Using token:', token ? 'Present' : 'Missing');
      
      const url = getApiUrl(`/admin/vps/users/search?email=${encodeURIComponent(searchEmail)}`);
      console.log('📡 Request URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📥 Response status:', response.status);
      const result = await response.json();
      console.log('📄 Response data:', result);
      
      if (result.success) {
        setSelectedUserId(result.data.id);
        setNewVPSLimit(result.data.max_vps_allowed || 5);
        console.log('✅ User found, fetching VPS overview...');
        await fetchUserVPSOverview(result.data.id);
      } else {
        console.log('❌ Search failed:', result.error);
        setError(result.error || 'User not found');
        setUserVPSOverview(null);
      }
    } catch (error) {
      console.error('💥 Error searching user:', error);
      setError('Failed to search user');
      setUserVPSOverview(null);
    } finally {
      setSearchingUser(false);
    }
  };

  const fetchUserVPSOverview = async (userId: string) => {
    try {
      console.log('🔍 Fetching VPS overview for user ID:', userId);
      const token = localStorage.getItem('token');
      console.log('🔑 Token available:', token ? 'Yes' : 'No');
      
      const url = getApiUrl(`/admin/vps/users/${userId}/vps-overview`);
      console.log('📡 Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📥 VPS Overview response status:', response.status);
      const result = await response.json();
      console.log('📄 VPS Overview response data:', result);
      
      if (result.success) {
        console.log('✅ Setting user VPS overview data:', result.data);
        setUserVPSOverview(result.data);
      } else {
        console.log('❌ VPS Overview failed:', result.error);
        setError(result.error || 'Failed to fetch user VPS overview');
      }
    } catch (error) {
      console.error('💥 Error fetching user VPS overview:', error);
      setError('Failed to fetch user VPS overview');
    }
  };

  const handleRecalculateUserCount = async (userId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/users/${userId}/recalculate-count`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        if (userVPSOverview) {
          fetchUserVPSOverview(userId);
        }
      } else {
        setError(result.error || 'Failed to recalculate VPS count');
      }
    } catch (error) {
      console.error('Error recalculating VPS count:', error);
      setError('Failed to recalculate VPS count');
    }
  };

  const handleUpdateUserLimit = async (userId: string, newLimit: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/users/${userId}/limits`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ max_vps_allowed: newLimit })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        if (userVPSOverview) {
          fetchUserVPSOverview(userId);
        }
      } else {
        setError(result.error || 'Failed to update VPS limit');
      }
    } catch (error) {
      console.error('Error updating VPS limit:', error);
      setError('Failed to update VPS limit');
    }
  };

  const handleTerminateVPS = async (instanceId: string, reason = 'Admin termination') => {
    if (!window.confirm('Are you sure you want to DELETE this VPS instance? This will permanently delete the VM from Proxmox and remove it from the database. This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${instanceId}/terminate`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message || 'VPS instance deleted successfully from Proxmox and database');
        fetchVPSInstances();
        fetchVPSStatistics();
      } else {
        setError(result.error || 'Failed to delete VPS instance');
      }
    } catch (error) {
      console.error('Error deleting VPS instance:', error);
      setError('Failed to delete VPS instance');
    }
  };

  const handleCleanupStuckVPS = async (userId?: string) => {
    if (!window.confirm(`Are you sure you want to clean up stuck VPS instances${userId ? ' for this user' : ' system-wide'}?`)) {
      return;
    }

    setCleanupProgress({ status: 'running', message: 'Cleaning up stuck VPS instances...' });
    
    try {
      const token = localStorage.getItem('token');
      const url = userId 
        ? getApiUrl(`/admin/vps/cleanup/stuck-vps/${userId}?hours=${cleanupHours}`)
        : getApiUrl(`/admin/vps/cleanup/stuck-vps?hours=${cleanupHours}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        setCleanupProgress({ 
          status: 'success', 
          message: result.message,
          details: result.data 
        });
        fetchVPSInstances();
        fetchVPSStatistics();
        if (userId && userVPSOverview) {
          fetchUserVPSOverview(userId);
        }
      } else {
        setError(result.error || 'Failed to cleanup stuck VPS instances');
        setCleanupProgress({ status: 'error', message: result.error });
      }
    } catch (error) {
      console.error('Error cleaning up stuck VPS:', error);
      setError('Failed to cleanup stuck VPS instances');
      setCleanupProgress({ status: 'error', message: 'Failed to cleanup stuck VPS instances' });
    }
  };

  const handleRecalculateAllCounts = async () => {
    if (!window.confirm('Are you sure you want to recalculate VPS counts for all users? This may take a while.')) {
      return;
    }

    setCleanupProgress({ status: 'running', message: 'Recalculating VPS counts for all users...' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/vps/recalculate-all-counts'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        setCleanupProgress({ 
          status: 'success', 
          message: result.message,
          details: result.data 
        });
        fetchVPSStatistics();
      } else {
        setError(result.error || 'Failed to recalculate all VPS counts');
        setCleanupProgress({ status: 'error', message: result.error });
      }
    } catch (error) {
      console.error('Error recalculating all VPS counts:', error);
      setError('Failed to recalculate all VPS counts');
      setCleanupProgress({ status: 'error', message: 'Failed to recalculate all VPS counts' });
    }
  };

  const handleBulkTerminate = async () => {
    if (selectedInstances.length === 0) {
      setError('Please select VPS instances to terminate');
      return;
    }

    if (!window.confirm(`Are you sure you want to terminate ${selectedInstances.length} VPS instances? This action cannot be undone.`)) {
      return;
    }

    for (const instanceId of selectedInstances) {
      await handleTerminateVPS(instanceId);
    }
    setSelectedInstances([]);
    fetchVPSInstances();
    fetchVPSStatistics();
  };

  // Sync VPS status with Proxmox - mark VMs as terminated if they no longer exist
  const handleSyncWithProxmox = async () => {
    if (!window.confirm('This will check all VPS instances against Proxmox and mark any that no longer exist as terminated. Continue?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/vps/sync-with-proxmox'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message || `Sync complete. ${result.data?.terminated || 0} VPS instances marked as terminated.`);
        // Refresh the VPS list
        await fetchVPSInstances();
        await fetchVPSStatistics();
      } else {
        setError(result.error || 'Failed to sync with Proxmox');
      }
    } catch (error) {
      console.error('Error syncing with Proxmox:', error);
      setError('Failed to sync with Proxmox');
    } finally {
      setLoading(false);
    }
  };

  // Fetch provisioning logs for a VPS instance
  const fetchProvisioningLogs = async (instanceId: string) => {
    try {
      setLoadingLogs(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${instanceId}/provisioning-logs`), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        const newLogs = result.data || [];
        setProvisioningLogs(newLogs);
        
        // Auto-scroll to bottom when new logs arrive
        setTimeout(() => {
          const consoleDiv = document.querySelector('[data-console-output]');
          if (consoleDiv) {
            consoleDiv.scrollTop = consoleDiv.scrollHeight;
          }
        }, 100);
        
        // If VPS is still creating, continue polling
        if (selectedInstanceForConsole && selectedInstanceForConsole.status === 'creating' && !logsPollInterval) {
          const interval = setInterval(() => {
            fetchProvisioningLogs(selectedInstanceForConsole.id);
          }, 2000);
          setLogsPollInterval(interval);
        }
      } else {
        setError('Failed to fetch provisioning logs');
      }
    } catch (error) {
      console.error('Error fetching provisioning logs:', error);
      setError('Failed to fetch provisioning logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  // Open console modal for a VPS instance
  const handleOpenConsole = async (instance: any) => {
    setSelectedInstanceForConsole(instance);
    setShowConsoleModal(true);
    await fetchProvisioningLogs(instance.id);
    
    // Auto-poll logs if VPS is still provisioning
    if (instance.status === 'creating') {
      const interval = setInterval(() => {
        fetchProvisioningLogs(instance.id);
      }, 2000); // Poll every 2 seconds
      setLogsPollInterval(interval);
    }
    
    // Auto-scroll to bottom after logs load
    setTimeout(() => {
      const consoleDiv = document.querySelector('[data-console-output]');
      if (consoleDiv) {
        consoleDiv.scrollTop = consoleDiv.scrollHeight;
      }
    }, 500);
  };

  // Close console modal and stop polling
  const handleCloseConsole = () => {
    setShowConsoleModal(false);
    setSelectedInstanceForConsole(null);
    setProvisioningLogs([]);
    if (logsPollInterval) {
      clearInterval(logsPollInterval);
      setLogsPollInterval(null);
    }
  };

  // Memory management handlers
  const handleOpenMemoryModal = (instance: VPSInstance) => {
    setSelectedInstanceForMemory(instance);
    setNewMemoryMB(instance.memory_mb);
    setMemoryUpdateResult(null);
    setShowMemoryModal(true);
  };

  const handleChangeMemory = async () => {
    if (!selectedInstanceForMemory) return;

    setMemoryChanging(true);
    setMemoryUpdateResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${selectedInstanceForMemory.id}/memory`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ memory_mb: newMemoryMB })
      });

      const result = await response.json();

      if (result.success) {
        const requiresRestart = result.data?.requiresRestart || false;
        setMemoryUpdateResult({
          success: true,
          message: `Memory configuration updated from ${(selectedInstanceForMemory.memory_mb / 1024).toFixed(1)} GB to ${(newMemoryMB / 1024).toFixed(1)} GB.`,
          requiresRestart
        });
        // Update the selected instance's memory locally
        setSelectedInstanceForMemory({...selectedInstanceForMemory, memory_mb: newMemoryMB});
        fetchVPSInstances(); // Refresh the list
      } else {
        setMemoryUpdateResult({
          success: false,
          message: result.error || 'Failed to update memory configuration'
        });
      }
    } catch (err: any) {
      setMemoryUpdateResult({
        success: false,
        message: err.message || 'Failed to update memory configuration'
      });
    } finally {
      setMemoryChanging(false);
    }
  };

  const handleRestartAfterMemoryChange = async () => {
    if (!selectedInstanceForMemory) return;

    setRestartingAfterMemory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${selectedInstanceForMemory.id}/restart`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setMemoryUpdateResult({
          success: true,
          message: 'VM is now restarting. Memory changes will take effect shortly.',
          requiresRestart: false
        });
        fetchVPSInstances();
      } else {
        setMemoryUpdateResult({
          success: false,
          message: `Failed to restart VM: ${result.error || 'Unknown error'}`
        });
      }
    } catch (err: any) {
      setMemoryUpdateResult({
        success: false,
        message: `Failed to restart VM: ${err.message || 'Unknown error'}`
      });
    } finally {
      setRestartingAfterMemory(false);
    }
  };

  // Networking management handlers
  const handleOpenNetworkingModal = async (instance: any) => {
    setSelectedInstanceForNetworking(instance);
    setShowNetworkingModal(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Fetch networking info
      const networkingResponse = await fetch(getApiUrl(`/admin/vps/instances/${instance.id}/networking`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const networkingResult = await networkingResponse.json();
      if (networkingResult.success) {
        console.log('Networking info received:', networkingResult.data);
        console.log('Hostname in networking info:', networkingResult.data?.hostname);
        setNetworkingInfo(networkingResult.data);
      }
      
      // Fetch connection info
      const connectionResponse = await fetch(getApiUrl(`/admin/vps/instances/${instance.id}/credentials/connection-info`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const connectionResult = await connectionResponse.json();
      if (connectionResult.success) {
        console.log('Connection info received:', connectionResult.data);
        console.log('Hostname in connection info:', connectionResult.data?.hostname);
        setConnectionInfo(connectionResult.data);
      } else {
        console.error('Connection info API error:', connectionResult.error);
        // Set connection info with data from instance as fallback
        const sshUsername = getDefaultUsernameForOS(instance.os_template);
        setConnectionInfo({
          ipv4_address: instance.ipv4_address || null,
          ipv6_address: instance.ipv6_address || null,
          ssh_commands: {
            by_ipv4: instance.ipv4_address ? `ssh ${sshUsername}@${instance.ipv4_address}` : null,
            by_ipv6: instance.ipv6_address ? `ssh -6 ${sshUsername}@${instance.ipv6_address}` : null
          },
          username: sshUsername,
          has_password_set: true,
          ssh_keys_count: (instance.ssh_keys || []).length,
          ssh_keys: instance.ssh_keys || []
        });
      }
    } catch (error) {
      console.error('Error fetching networking/connection info:', error);
      // Set connection info with data from instance as fallback
      const sshUsername = getDefaultUsernameForOS(instance.os_template);
      setConnectionInfo({
        ipv4_address: instance.ipv4_address || null,
        ipv6_address: instance.ipv6_address || null,
        ssh_commands: {
          by_ipv4: instance.ipv4_address ? `ssh ${sshUsername}@${instance.ipv4_address}` : null,
          by_ipv6: instance.ipv6_address ? `ssh -6 ${sshUsername}@${instance.ipv6_address}` : null
        },
        username: sshUsername,
        has_password_set: true,
        ssh_keys_count: (instance.ssh_keys || []).length,
        ssh_keys: instance.ssh_keys || []
      });
    }
  };

  const handleAttachIPv6 = async (instanceId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${instanceId}/network/attach-ipv6`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess('IPv6 address attached successfully');
        fetchVPSInstances();
        await handleOpenNetworkingModal(selectedInstanceForNetworking);
      } else {
        setError(result.error || 'Failed to attach IPv6');
      }
    } catch (error) {
      console.error('Error attaching IPv6:', error);
      setError('Failed to attach IPv6 address');
    }
  };

  const handleDetachIPv6 = async (instanceId: string) => {
    if (!window.confirm('Are you sure you want to detach IPv6 from this VPS?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${instanceId}/network/detach-ipv6`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess('IPv6 address detached successfully');
        fetchVPSInstances();
        await handleOpenNetworkingModal(selectedInstanceForNetworking);
      } else {
        setError(result.error || 'Failed to detach IPv6');
      }
    } catch (error) {
      console.error('Error detaching IPv6:', error);
      setError('Failed to detach IPv6 address');
    }
  };

  const handleCreateTunnel = async (instanceId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${instanceId}/network/create-tunnel`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess('DNS record created successfully');
        await handleOpenNetworkingModal(selectedInstanceForNetworking);
      } else {
        setError(result.error || 'Failed to create DNS record');
      }
    } catch (error) {
      console.error('Error creating DNS record:', error);
      setError('Failed to create DNS record');
    }
  };

  // Credential management handlers
  const handleResetPassword = async (instanceId: string) => {
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${instanceId}/credentials/reset-password`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess('Password reset successfully');
        setNewPassword('');
        setShowPasswordReset(false);
        await handleOpenNetworkingModal(selectedInstanceForNetworking);
      } else {
        setError(result.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Failed to reset password');
    }
  };

  const handleAddSSHKey = async (instanceId: string) => {
    if (!newSSHKey) {
      setError('Please enter an SSH public key');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${instanceId}/credentials/add-ssh-key`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sshKey: newSSHKey })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess('SSH key added successfully');
        setNewSSHKey('');
        setShowSSHKeyAdd(false);
        await handleOpenNetworkingModal(selectedInstanceForNetworking);
      } else {
        setError(result.error || 'Failed to add SSH key');
      }
    } catch (error) {
      console.error('Error adding SSH key:', error);
      setError('Failed to add SSH key');
    }
  };

  const handleRemoveSSHKey = async (instanceId: string, sshKey: string) => {
    if (!window.confirm('Are you sure you want to remove this SSH key?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/vps/instances/${instanceId}/credentials/remove-ssh-key`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sshKey })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess('SSH key removed successfully');
        await handleOpenNetworkingModal(selectedInstanceForNetworking);
      } else {
        setError(result.error || 'Failed to remove SSH key');
      }
    } catch (error) {
      console.error('Error removing SSH key:', error);
      setError('Failed to remove SSH key');
    }
  };

  // Provision VPS handler
  const handleProvisionVPS = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!provisionForm.customer_id || !provisionForm.plan_id || !provisionForm.name || !provisionForm.proxmox_host_id) {
      setError('Please fill in all required fields (Customer, Host Server, Plan, and Name)');
      return;
    }

    // Validate OS template selection
    if (!provisionForm.template_id && !provisionForm.os_template) {
      setError('Please select an Operating System');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/vps/instances'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: provisionForm.customer_id, // This is actually user_id, backend will find customer
          plan_id: provisionForm.plan_id,
          name: provisionForm.name,
          os_template: provisionForm.os_template,
          template_id: provisionForm.template_id || null, // Dynamic OS template ID from database
          sshUsername: provisionForm.sshUsername || 'ubuntu',
          initialPassword: provisionForm.initialPassword || null,
          sshPublicKeys: provisionForm.sshPublicKeys,
          proxmox_host_id: parseInt(provisionForm.proxmox_host_id) // Include selected host
        })
      });

      const result = await response.json();
      if (result.success) {
        // Only show password modal if password was auto-generated (not admin-provided)
        // If admin provided their own password, they don't need to see it again
        const passwordGenerated = result.data?.passwordGenerated;
        const password = result.password || result.data?.initialPassword;

        if (passwordGenerated && password) {
          // Only show password modal when it was auto-generated
          setDisplayedPassword(password);
          setPasswordCopied(false);
          setShowPasswordModal(true);
          setShowProvisionModal(false); // Close provision modal first
        } else {
          setSuccess(result.message || 'VPS provisioning initiated successfully!');
        }
        
        setShowProvisionModal(false);
        setProvisionForm({
          customer_id: '',
          plan_id: '',
          name: '',
          os_template: 'ubuntu:22.04',
          template_id: '',
          sshUsername: 'ubuntu',
          initialPassword: '',
          sshPublicKeys: [],
          proxmox_host_id: ''
        });
        setSelectedOSTemplate(null);
        setProvisionSSHKey('');

        // Switch to "All" filter so user can see the new VPS immediately (it will be in "creating" status)
        setFilterStatus('all');

        // Refresh immediately and again after a short delay to catch the new VPS
        fetchVPSInstances();
        fetchVPSStatistics();
        setTimeout(() => {
          fetchVPSInstances();
          fetchVPSStatistics();
        }, 2000);

        // Continue polling for status updates while VPS is being created
        const pollInterval = setInterval(async () => {
          await fetchVPSInstances();
          await fetchVPSStatistics();
        }, 10000); // Poll every 10 seconds

        // Stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
        }, 300000);
      } else {
        setError(result.error || 'Failed to provision VPS');
      }
    } catch (error) {
      console.error('Error provisioning VPS:', error);
      setError('Failed to provision VPS');
    }
  };

  const handleAddProvisionSSHKey = () => {
    if (provisionSSHKey.trim()) {
      setProvisionForm({
        ...provisionForm,
        sshPublicKeys: [...provisionForm.sshPublicKeys, provisionSSHKey.trim()]
      });
      setProvisionSSHKey('');
    }
  };

  const handleRemoveProvisionSSHKey = (index: number) => {
    setProvisionForm({
      ...provisionForm,
      sshPublicKeys: provisionForm.sshPublicKeys.filter((_, i) => i !== index)
    });
  };

  // Filter and sort VPS instances
  const filteredInstances = vpsInstances.filter(instance => {
    const matchesStatus = filterStatus === 'all' || instance.status === filterStatus;
    const matchesServer = filterServer === 'all' || instance.lxd_server_id === filterServer;
    const matchesSearch = !searchTerm ||
      instance.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (instance.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (instance.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (instance.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      instance.hostname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (instance.ipv4_address || '').includes(searchTerm);
    
    return matchesStatus && matchesServer && matchesSearch;
  }).sort((a, b) => {
    let aValue = a[sortBy as keyof VPSInstance];
    let bValue = b[sortBy as keyof VPSInstance];
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      description: '',
      cpu_cores: 1,
      memory_mb: 1024,
      disk_gb: 25,
      bandwidth_gb: 1000,
      monthly_price: 5.00,
      features: {
        ssd_storage: true,
        backup_included: false,
        monitoring_included: true,
        load_balancer: false,
        dedicated_ip: true
      },
      is_active: true
    });
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'stopped':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'creating':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'terminated':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'online':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'offline':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
      case 'online':
        return (
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
          </span>
        );
      case 'stopped':
      case 'offline':
        return <span className="inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>;
      case 'creating':
        return (
          <svg className="animate-spin h-3 w-3 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'suspended':
      case 'maintenance':
        return <span className="inline-flex rounded-full h-2.5 w-2.5 bg-yellow-500"></span>;
      case 'terminated':
        return <span className="inline-flex rounded-full h-2.5 w-2.5 bg-gray-900"></span>;
      default:
        return <span className="inline-flex rounded-full h-2.5 w-2.5 bg-gray-400"></span>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="relative p-8">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-violet-50/30"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 to-violet-100/30 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>

          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-violet-600 rounded-2xl flex items-center justify-center shadow-xl shadow-blue-500/25">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 mb-2 bg-blue-50 border border-blue-200 rounded-full">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                  </span>
                  <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Infrastructure Control</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900">VPS Management</h1>
                <p className="text-gray-500 mt-1">Manage virtual servers, monitor resources, and control infrastructure</p>
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-gray-400 uppercase tracking-wider">Total Capacity</p>
                <p className="text-2xl font-bold text-gray-900">{vpsInstances.length} <span className="text-sm font-normal text-gray-500">instances</span></p>
              </div>
              <div className="w-px h-12 bg-gray-200"></div>
              <div className="text-right">
                <p className="text-xs text-gray-400 uppercase tracking-wider">Active</p>
                <p className="text-2xl font-bold text-emerald-600">{vpsInstances.filter(i => i.status === 'running').length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
          <button onClick={() => setError('')} className="p-1 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-100 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium text-emerald-800">{success}</p>
          </div>
          <button onClick={() => setSuccess('')} className="p-1 rounded-lg text-emerald-400 hover:text-emerald-600 hover:bg-emerald-100 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Tab Navigation - Enterprise Style */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <nav className="flex overflow-x-auto scrollbar-hide">
          {[
            { id: 'instances', name: 'Instances', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
              </svg>
            )},
            { id: 'plans', name: 'Plans', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            )},
            { id: 'os-templates', name: 'OS Templates', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
            )},
            { id: 'users', name: 'Users', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            )},
            { id: 'cleanup', name: 'Health', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            )},
            { id: 'proxmox-hosts', name: 'Hosts', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            )},
            { id: 'host-firewall', name: 'Host Firewall', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            )},
            { id: 'firewall', name: 'VPS Firewall', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            )},
            { id: 'ipv4-pool', name: 'IPv4 Pool', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
            )},
            { id: 'backups', name: 'Backups', icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
            )}
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`group relative flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeTab === tab.id
                  ? 'text-blue-600 bg-blue-50/80'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <span className={`transition-colors ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}>
                {tab.icon}
              </span>
              {tab.name}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* VPS Instances Tab */}
      {activeTab === 'instances' && (
        <div className="space-y-6">
          {/* Enterprise Statistics Cards */}
          {stats && stats.insights && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Total Instances Card */}
              <div className="relative overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-full"></div>
                </div>
                <div className="relative p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Instances</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.insights.total_instances}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Running Card */}
              <div className="relative overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 rounded-full"></div>
                </div>
                <div className="relative p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Running</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.insights.status_breakdown.running || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stuck Creating Card */}
              <div className="relative overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-600/5 rounded-full"></div>
                </div>
                <div className="relative p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg shadow-red-500/25">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Stuck Creating</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.insights.stuck_instances}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Count Issues Card */}
              <div className="relative overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-full"></div>
                </div>
                <div className="relative p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/25">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sync Issues</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.insights.count_discrepancies}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Today's Activity Card */}
              <div className="relative overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 to-violet-600/5 rounded-full"></div>
                </div>
                <div className="relative p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg flex items-center justify-center shadow-lg shadow-violet-500/25">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created Today</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.insights.recent_activity.last_24h_created}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Clean Header with Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            {/* Header Row */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              {/* Left: Title and Status Filters */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900 whitespace-nowrap">VPS Instances</h3>
                <div className="flex flex-wrap gap-1.5">
                  {vpsInstances.filter(i => i.status === 'creating').length > 0 && (
                    <button
                      onClick={() => setFilterStatus('creating')}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        filterStatus === 'creating'
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                      }`}
                    >
                      Creating ({vpsInstances.filter(i => i.status === 'creating').length})
                    </button>
                  )}
                  <button
                    onClick={() => setFilterStatus('running')}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      filterStatus === 'running'
                        ? 'bg-green-600 text-white'
                        : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                    }`}
                  >
                    Running ({vpsInstances.filter(i => i.status === 'running').length})
                  </button>
                  <button
                    onClick={() => setFilterStatus('stopped')}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      filterStatus === 'stopped'
                        ? 'bg-amber-600 text-white'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                    }`}
                  >
                    Stopped ({vpsInstances.filter(i => i.status === 'stopped').length})
                  </button>
                  <button
                    onClick={() => setFilterStatus('all')}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      filterStatus === 'all'
                        ? 'bg-gray-600 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    All ({vpsInstances.length})
                  </button>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2">
                {/* Sync/Refresh Dropdown or buttons */}
                <button
                  onClick={refreshAllStatuses}
                  disabled={refreshingStatuses}
                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Refresh statuses from Proxmox"
                >
                  {refreshingStatuses ? (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={handleSyncWithProxmox}
                  disabled={loading}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  Sync
                </button>
                <div className="w-px h-6 bg-gray-200"></div>
                <button
                  onClick={() => {
                    if (allUsers.length === 0) {
                      fetchAllUsers();
                    }
                    setShowProvisionModal(true);
                  }}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New VPS
                </button>
              </div>
            </div>

            {/* Auto-refresh indicator - subtle footer */}
            <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-2 mt-2">
              <label className="flex items-center gap-1.5 cursor-pointer hover:text-gray-600">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3 w-3"
                />
                <span>Auto-refresh</span>
              </label>
              {lastRefreshed && (
                <span>Updated {lastRefreshed.toLocaleTimeString()}</span>
              )}
            </div>

            {/* Search and Sort Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Name, email, IP..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="created_at">Created Date</option>
                  <option value="name">Name</option>
                  <option value="status">Status</option>
                  <option value="customer_email">Customer</option>
                  <option value="cpu_cores">Resources</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
            
            {selectedInstances.length > 0 && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
                <span className="text-sm text-yellow-800">
                  {selectedInstances.length} instances selected
                </span>
                <div className="space-x-2">
                  <button
                    onClick={handleBulkTerminate}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                  >
                    Terminate Selected
                  </button>
                  <button
                    onClick={() => setSelectedInstances([])}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* VPS Instances Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">VPS Instances</h3>
                    <p className="text-xs text-gray-500">{filteredInstances.length} instances found</p>
                  </div>
                </div>
                {/* Storage Summary with Color-Coded Warning */}
                {filteredInstances.length > 0 && (() => {
                  const totalUsed = filteredInstances.reduce((sum, i) => sum + (i.actual_disk_used_gb || 0), 0);
                  const totalAllocated = filteredInstances.reduce((sum, i) => sum + (i.disk_gb || 0), 0);
                  // Real total physical storage = sum of the VPS-enabled hosts'
                  // capacity (from the live hosts list), NOT a hardcoded single
                  // server. The old hardcoded 836 divided ALL instances' usage
                  // (across every host) by one server's disk → false "Critical".
                  const vpsHosts = (proxmoxHosts || []).filter((h: any) => h.vps_enabled && h.status === 'active');
                  const physicalStorage = vpsHosts.reduce((sum: number, h: any) => sum + (Number(h.storage_gb) || 0), 0) || 836;
                  const usagePercent = physicalStorage > 0 ? (totalUsed / physicalStorage) * 100 : 0;

                  // Color coding based on physical storage usage
                  let statusColor = 'text-emerald-700';
                  let statusBg = 'bg-emerald-50 border-emerald-200';
                  let statusText = 'Healthy';
                  let progressColor = 'bg-emerald-500';

                  if (usagePercent >= 85) {
                    statusColor = 'text-red-700';
                    statusBg = 'bg-red-50 border-red-200';
                    statusText = 'Critical';
                    progressColor = 'bg-red-500';
                  } else if (usagePercent >= 70) {
                    statusColor = 'text-amber-700';
                    statusBg = 'bg-amber-50 border-amber-200';
                    statusText = 'Warning';
                    progressColor = 'bg-amber-500';
                  }

                  return (
                    <div className="flex items-center gap-4">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${statusBg}`}>
                        <span className={`w-2 h-2 rounded-full ${progressColor}`}></span>
                        <span className={`text-sm font-semibold ${statusColor}`}>{statusText}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full ${progressColor} transition-all duration-500`} style={{ width: `${Math.min(usagePercent, 100)}%` }}></div>
                        </div>
                        <span className="text-sm text-gray-600">
                          <span className="font-semibold text-gray-900">{totalUsed.toFixed(0)}GB</span>
                          <span className="text-gray-400"> / {physicalStorage}GB</span>
                          <span className="text-gray-400 ml-1">({usagePercent.toFixed(1)}%)</span>
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="w-10 px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedInstances.length === filteredInstances.length && filteredInstances.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedInstances(filteredInstances.map(i => i.id));
                          } else {
                            setSelectedInstances([]);
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Instance</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Specs</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Network</th>
                    <th className="w-36 px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredInstances.map((instance, index) => {
                    const displayStatus = instance.live_status || instance.status;
                    const op = vpsOperations.get(instance.id);
                    const isOperating = op?.status === 'in_progress';
                    return (
                    <tr key={instance.id} className={`hover:bg-blue-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedInstances.includes(instance.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedInstances([...selectedInstances, instance.id]);
                            } else {
                              setSelectedInstances(selectedInstances.filter(id => id !== instance.id));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                        />
                      </td>
                      {/* Instance Column - Combined status, name, host */}
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-3">
                          {/* Status indicator */}
                          <div className="flex-shrink-0 mt-1">
                            <div className={`w-3 h-3 rounded-full ${displayStatus === 'running' ? 'bg-emerald-500 shadow-sm shadow-emerald-500/50' : displayStatus === 'stopped' ? 'bg-red-500' : displayStatus === 'creating' ? 'bg-blue-500 animate-pulse' : 'bg-gray-400'}`}></div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900 truncate">{instance.name}</span>
                              {instance.proxmox_vmid && (
                                <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-mono">#{instance.proxmox_vmid}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                              <span className="capitalize">{displayStatus}</span>
                              <span className="text-gray-300">|</span>
                              <span>{instance.os_template}</span>
                            </div>
                            <div className="mt-1 text-xs text-gray-400">
                              Host: {instance.proxmox_host_display || instance.proxmox_node || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* Customer Column - Name, email, ID */}
                      <td className="px-4 py-3">
                        <div className="min-w-0 max-w-[180px]">
                          <div className="font-medium text-gray-900 truncate">
                            {instance.first_name && instance.last_name
                              ? `${instance.first_name} ${instance.last_name}`
                              : instance.first_name || instance.last_name || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{instance.email || 'N/A'}</div>
                          <div className="text-xs text-gray-400 mt-0.5 font-mono">
                            {instance.customer_id ? `#${instance.customer_id.slice(0, 8)}` : ''}
                          </div>
                        </div>
                      </td>
                      {/* Specs Column - CPU, RAM, Disk */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {instance.cpu_cores} vCPU
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            {Math.round(instance.memory_mb / 1024)}GB
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {instance.actual_disk_used_gb !== null && instance.actual_disk_used_gb !== undefined
                              ? `${instance.actual_disk_used_gb}/${instance.disk_gb}GB`
                              : `${instance.disk_gb}GB`}
                          </span>
                        </div>
                      </td>
                      {/* Network Column - IP */}
                      <td className="px-4 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1">
                            <span className={`font-mono text-sm ${instance.ipv4_address ? 'text-gray-900' : 'text-amber-600'}`}>
                              {instance.ipv4_address || 'Pending'}
                            </span>
                            {instance.ipv4_address && (
                              <button
                                onClick={() => navigator.clipboard.writeText(instance.ipv4_address || '')}
                                className="p-0.5 hover:bg-gray-100 rounded"
                                title="Copy"
                              >
                                <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 truncate">{instance.hostname}</div>
                        </div>
                      </td>
                      {/* Actions Column */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Power buttons based on status */}
                          {displayStatus === 'stopped' ? (
                            <button
                              onClick={() => handleControlVPS(instance.id, 'start')}
                              disabled={isOperating}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white rounded transition-colors ${isOperating ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                              title="Start"
                            >
                              {isOperating ? (
                                <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                </svg>
                              )}
                              Start
                            </button>
                          ) : displayStatus === 'running' ? (
                            <>
                              <button
                                onClick={() => handleControlVPS(instance.id, 'stop')}
                                disabled={isOperating}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-l transition-colors ${isOperating ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                                title="Stop"
                              >
                                {isOperating && op?.action === 'stop' ? (
                                  <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M5.75 3A1.75 1.75 0 004 4.75v10.5c0 .966.784 1.75 1.75 1.75h8.5A1.75 1.75 0 0016 15.25V4.75A1.75 1.75 0 0014.25 3h-8.5z" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => handleControlVPS(instance.id, 'restart')}
                                disabled={isOperating}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-r transition-colors ${isOperating ? 'bg-gray-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700'}`}
                                title="Restart"
                              >
                                {isOperating && op?.action === 'restart' ? (
                                  <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                )}
                              </button>
                            </>
                          ) : instance.status === 'creating' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded">
                              <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                              </svg>
                              Creating
                            </span>
                          ) : null}

                            {/* More Actions Dropdown */}
                          {/* More Actions Menu */}
                          <div className="relative">
                            <button
                              onClick={() => setOpenActionMenu(openActionMenu === instance.id ? null : instance.id)}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="More actions"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                              </svg>
                            </button>
                            {openActionMenu === instance.id && (
                              <>
                                <div className="fixed inset-0 z-10" onClick={() => setOpenActionMenu(null)} />
                                <div className="absolute right-0 z-20 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                                  <button
                                    onClick={() => { handleOpenConsole(instance); setOpenActionMenu(null); }}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                                  >
                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Console
                                  </button>
                                  <button
                                    onClick={() => { handleOpenNetworkingModal(instance); setOpenActionMenu(null); }}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                                  >
                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                    </svg>
                                    Network
                                  </button>
                                  <button
                                    onClick={() => { handleOpenMemoryModal(instance); setOpenActionMenu(null); }}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                                  >
                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                    </svg>
                                    Resources
                                  </button>
                                  <div className="border-t border-gray-100 my-1"></div>
                                  <button
                                    onClick={() => { handleTerminateVPS(instance.id); setOpenActionMenu(null); }}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
              
              {filteredInstances.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">No VPS instances found matching your criteria</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* VPS Plans Tab */}
      {activeTab === 'plans' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">VPS Plans</h3>
            <button
              onClick={() => setShowCreatePlanForm(true)}
              className="px-4 py-2 bg-brand-orange text-white rounded-lg hover:bg-secondary-600"
            >
              Create Plan
            </button>
          </div>

          {/* Create Plan Form */}
          {showCreatePlanForm && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h4 className="text-lg font-semibold mb-4">{editingPlan ? `Edit VPS Plan: ${editingPlan.name}` : 'Create VPS Plan'}</h4>
              <form onSubmit={handleCreatePlan} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Plan Name</label>
                    <input
                      type="text"
                      value={planForm.name}
                      onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={planForm.monthly_price}
                      onChange={(e) => setPlanForm({...planForm, monthly_price: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={planForm.description}
                    onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CPU Cores</label>
                    <input
                      type="number"
                      value={planForm.cpu_cores}
                      onChange={(e) => setPlanForm({...planForm, cpu_cores: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      min="1"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Memory (MB)</label>
                    <input
                      type="number"
                      value={planForm.memory_mb}
                      onChange={(e) => setPlanForm({...planForm, memory_mb: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      min="512"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Disk (GB)</label>
                    <input
                      type="number"
                      value={planForm.disk_gb}
                      onChange={(e) => setPlanForm({...planForm, disk_gb: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      min="10"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bandwidth (GB)</label>
                    <input
                      type="number"
                      value={planForm.bandwidth_gb}
                      onChange={(e) => setPlanForm({...planForm, bandwidth_gb: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                      min="100"
                      required
                    />
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-brand-orange text-white rounded-lg hover:bg-secondary-600"
                  >
                    {editingPlan ? 'Save Changes' : 'Create Plan'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreatePlanForm(false);
                      setEditingPlan(null);
                      resetPlanForm();
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vpsPlans.map((plan) => (
              <div key={plan.id} className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 relative overflow-hidden group">
                {/* Glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-xl font-bold text-gray-900">{plan.name}</h4>
                  <span className={`px-3 py-2 text-sm font-semibold rounded-full shadow-sm ${plan.is_active ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'}`}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <p className="text-gray-700 text-base mb-6 leading-relaxed">{plan.description}</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-base font-medium text-gray-700">Monthly Price:</span>
                    <span className="text-2xl font-bold text-primary-navy">${plan.monthly_price}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                      <div className="text-sm font-medium text-gray-600">CPU Cores</div>
                      <div className="text-lg font-bold text-gray-900">{plan.cpu_cores}</div>
                    </div>
                    <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                      <div className="text-sm font-medium text-gray-600">Memory</div>
                      <div className="text-lg font-bold text-gray-900">{Math.round(plan.memory_mb / 1024)}GB</div>
                    </div>
                    <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                      <div className="text-sm font-medium text-gray-600">Storage</div>
                      <div className="text-lg font-bold text-gray-900">{plan.disk_gb}GB</div>
                    </div>
                    <div className="bg-white/50 rounded-xl p-3 text-center border border-gray-200">
                      <div className="text-sm font-medium text-gray-600">Bandwidth</div>
                      <div className="text-lg font-bold text-gray-900">{plan.bandwidth_gb}GB</div>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => handleStartEditPlan(plan)}
                    className="flex-1 bg-gradient-to-r from-primary-navy to-primary-800 text-white py-3 px-4 rounded-xl font-semibold hover:from-primary-800 hover:to-primary-900 transition-all duration-200 shadow-lg relative overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl"></div>
                    <span className="relative z-10">Edit</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeletePlan(plan)}
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg relative overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl"></div>
                    <span className="relative z-10">Delete</span>
                  </button>
                </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {!showUserDetails ? (
            /* User List View */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">User Management</h3>
                <p className="text-gray-600">Manage users, VPS limits, and resolve account issues</p>
              </div>

              {/* Search Bar */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">🔍</span>
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search users by email, name..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-lg"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilteredUsers(allUsers);
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <span className="text-gray-400 hover:text-gray-600">✕</span>
                    </button>
                  )}
                </div>
                
                {/* Search Results Summary */}
                <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
                  <div>
                    {searchTerm ? (
                      <>Showing {filteredUsers.length} results for "{searchTerm}"</>
                    ) : (
                      <>Showing {filteredUsers.length} users</>
                    )}
                  </div>
                  <div>
                    Page {currentPage} of {Math.ceil(filteredUsers.length / usersPerPage) || 1}
                  </div>
                </div>
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="text-4xl mb-4">⏳</div>
                      <p className="text-lg text-gray-600">Loading users...</p>
                    </div>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="text-6xl mb-4">👥</div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {searchTerm ? 'No users found' : 'No users available'}
                      </h4>
                      <p className="text-gray-500 mb-4">
                        {searchTerm ? `No users match "${searchTerm}"` : 'No users are registered in the system'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VPS Usage</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUsers
                        .slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage)
                        .map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                                  {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.first_name} {user.last_name}
                                </div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{user.current_vps_count}/{user.max_vps_allowed}</span>
                                {user.vps_info.running_instances > 0 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {user.vps_info.running_instances} running
                                  </span>
                                )}
                                {user.vps_info.stuck_instances > 0 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    {user.vps_info.stuck_instances} stuck
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {user.vps_info.has_issues ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  ⚠️ Issues
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✅ Healthy
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => selectUser(user)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-brand-orange bg-brand-orange/10 hover:bg-brand-orange/20 transition-colors"
                              >
                                📊 Manage
                              </button>
                              {user.vps_info.has_issues && (
                                <button
                                  onClick={() => selectUser(user)}
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 transition-colors"
                                >
                                  🔧 Fix
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              {filteredUsers.length > usersPerPage && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((currentPage - 1) * usersPerPage) + 1} to {Math.min(currentPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} results
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-700">
                      Page {currentPage} of {Math.ceil(filteredUsers.length / usersPerPage)}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(Math.ceil(filteredUsers.length / usersPerPage), currentPage + 1))}
                      disabled={currentPage >= Math.ceil(filteredUsers.length / usersPerPage)}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* User Details View */
            <div className="space-y-6">
              {/* Back Button */}
              <button
                onClick={() => {
                  setShowUserDetails(false);
                  setSelectedUserId('');
                  setUserVPSOverview(null);
                }}
                className="inline-flex items-center space-x-2 text-brand-orange hover:text-secondary-600 font-medium"
              >
                <span>←</span>
                <span>Back to User List</span>
              </button>

            {userVPSOverview && (
              <div className="space-y-6">
                {/* User Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">User Information</h4>
                    <p className="text-sm text-gray-600 mb-1">{userVPSOverview.user.email}</p>
                    <p className="text-lg font-medium text-gray-900">{userVPSOverview.user.first_name} {userVPSOverview.user.last_name}</p>
                    <p className="text-xs text-gray-500 mt-2">ID: {userVPSOverview.user.id.substring(0, 8)}...</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-3">VPS Usage</h4>
                    <p className="text-3xl font-bold text-blue-600">
                      {userVPSOverview.counts.actual_active_count} / {userVPSOverview.counts.max_allowed}
                    </p>
                    <div className="w-full bg-blue-200 rounded-full h-2 mt-3">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${(userVPSOverview.counts.actual_active_count / userVPSOverview.counts.max_allowed) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-blue-700 mt-2">
                      Stored: {userVPSOverview.counts.stored_count} | Running: {userVPSOverview.counts.running_count}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6 border border-yellow-200">
                    <h4 className="font-semibold text-yellow-900 mb-3">Issues Detected</h4>
                    <p className="text-3xl font-bold text-yellow-600">{userVPSOverview.counts.stuck_count}</p>
                    <p className="text-sm text-yellow-700 mt-2">Stuck instances</p>
                    {userVPSOverview.counts.stored_count !== userVPSOverview.counts.actual_active_count && (
                      <p className="text-xs text-red-600 mt-2">⚠️ Count mismatch detected</p>
                    )}
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                    <h4 className="font-semibold text-green-900 mb-3">Active Subscriptions</h4>
                    <p className="text-3xl font-bold text-green-600">{userVPSOverview.subscriptions}</p>
                    <p className="text-sm text-green-700 mt-2">Total subscriptions</p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Quick Actions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() => handleRecalculateUserCount(selectedUserId)}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <span>🔄</span>
                      <span>Recalculate VPS Count</span>
                    </button>
                    
                    <button
                      onClick={() => handleCleanupStuckVPS(selectedUserId)}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                    >
                      <span>🧹</span>
                      <span>Cleanup Stuck VPS</span>
                    </button>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={newVPSLimit}
                        onChange={(e) => setNewVPSLimit(parseInt(e.target.value))}
                        className="w-20 px-3 py-3 border border-gray-300 rounded-lg text-center"
                        min="1"
                        max="50"
                      />
                      <button
                        onClick={() => handleUpdateUserLimit(selectedUserId, newVPSLimit)}
                        className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Update Limit
                      </button>
                    </div>
                  </div>
                </div>

                {/* VPS Instances Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h4 className="text-lg font-semibold text-gray-900">User's VPS Instances</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Container</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {userVPSOverview.vps_instances.map((vps: any) => (
                          <tr key={vps.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{vps.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{vps.container_name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border ${getStatusColor(vps.status)}`}>
                                <span className="mr-1">{getStatusIcon(vps.status)}</span>
                                {vps.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {vps.plan_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(vps.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleTerminateVPS(vps.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete VPS from Proxmox and database"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            </div>
          )}
        </div>
      )}

      {/* System Health Tab */}
      {/* Image Library Tab removed - libvirt uses cloud images automatically */}

      {activeTab === 'cleanup' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg shadow-sm border border-blue-200 p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">System Health & Cleanup Tools</h3>
            <p className="text-gray-700">Maintain system health by cleaning up stuck instances and fixing count discrepancies</p>
          </div>
          
          {/* System Health Overview */}
          {stats && stats.insights && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">System Overview</h4>
                  <span className={`text-2xl ${stats.insights.stuck_instances + stats.insights.count_discrepancies === 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {stats.insights.stuck_instances + stats.insights.count_discrepancies === 0 ? '✅' : '⚠️'}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Issues</span>
                    <span className="text-2xl font-bold text-gray-900">
                      {stats.insights.stuck_instances + stats.insights.count_discrepancies}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>🔴 {stats.insights.stuck_instances} stuck instances</div>
                    <div>🟡 {stats.insights.count_discrepancies} count mismatches</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">VPS Status Distribution</h4>
                <div className="space-y-2">
                  {Object.entries(stats.insights.status_breakdown).map(([status, count]) => {
                    const percentage = ((count as number) / stats.insights.total_instances) * 100;
                    return (
                      <div key={status}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize text-gray-700">{status}</span>
                          <span className="font-medium">{count as number}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              status === 'running' ? 'bg-green-600' :
                              status === 'stopped' ? 'bg-red-600' :
                              status === 'creating' ? 'bg-blue-600' :
                              'bg-gray-600'
                            }`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h4>
                <div className="text-center">
                  <p className="text-4xl font-bold text-green-600">{stats.insights.recent_activity.last_24h_created}</p>
                  <p className="text-sm text-gray-600 mt-2">VPS created in last 24 hours</p>
                </div>
                {stats.insights.recent_activity.recent_instances.length > 0 && (
                  <div className="mt-4 text-xs text-gray-500 space-y-1 max-h-24 overflow-y-auto">
                    {stats.insights.recent_activity.recent_instances.slice(0, 3).map((vps: any) => (
                      <div key={vps.id} className="truncate">
                        {vps.name} - {new Date(vps.created_at).toLocaleTimeString()}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Cleanup Tools */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">🧹</span>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Cleanup Stuck VPS Instances</h4>
                  <p className="text-sm text-gray-600">Remove VPS instances stuck in "creating" status</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>What this does:</strong> Finds all VPS instances that have been stuck in "creating" 
                  status for longer than the specified time and terminates them. This also updates user VPS 
                  counts automatically.
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time threshold (hours)</label>
                  <input
                    type="number"
                    value={cleanupHours}
                    onChange={(e) => setCleanupHours(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                    min="1"
                    max="168"
                  />
                  <p className="text-xs text-gray-500 mt-1">VPS stuck for more than {cleanupHours} hour{cleanupHours > 1 ? 's' : ''} will be cleaned</p>
                </div>
                
                <button
                  onClick={() => handleCleanupStuckVPS()}
                  className="w-full px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium"
                >
                  🧹 Run System-Wide Cleanup
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">🔄</span>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Recalculate All VPS Counts</h4>
                  <p className="text-sm text-gray-600">Fix count discrepancies for all users</p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>What this does:</strong> Goes through every user in the system and recalculates 
                  their actual VPS count based on active instances. Fixes any mismatches between stored 
                  counts and actual instances.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="text-sm text-gray-700 space-y-2">
                  <div className="flex items-center">
                    <span className="mr-2">📊</span>
                    <span>Current discrepancies: <strong>{stats?.insights?.count_discrepancies || 0}</strong></span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">👥</span>
                    <span>Users affected: <strong>{stats?.insights?.count_discrepancies || 0}</strong></span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleRecalculateAllCounts}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                🔄 Recalculate All User Counts
              </button>
            </div>
          </div>

          {/* Cleanup Progress */}
          {cleanupProgress && (
            <div className={`rounded-lg p-6 ${
              cleanupProgress.status === 'running' ? 'bg-blue-50 border-blue-200' :
              cleanupProgress.status === 'success' ? 'bg-green-50 border-green-200' :
              'bg-red-50 border-red-200'
            } border`}>
              <div className="flex items-center space-x-3">
                <span className="text-2xl">
                  {cleanupProgress.status === 'running' ? '⏳' :
                   cleanupProgress.status === 'success' ? '✅' : '❌'}
                </span>
                <div className="flex-1">
                  <p className={`font-medium ${
                    cleanupProgress.status === 'running' ? 'text-blue-900' :
                    cleanupProgress.status === 'success' ? 'text-green-900' :
                    'text-red-900'
                  }`}>
                    {cleanupProgress.message}
                  </p>
                  {cleanupProgress.details && (
                    <div className="mt-2 text-sm text-gray-700">
                      {cleanupProgress.details.total_found !== undefined && (
                        <p>Found: {cleanupProgress.details.total_found} | Cleaned: {cleanupProgress.details.cleaned_up}</p>
                      )}
                      {cleanupProgress.details.totalUsers !== undefined && (
                        <p>Users processed: {cleanupProgress.details.totalUsers} | Updated: {cleanupProgress.details.updated}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Memory Management Modal */}
      {showMemoryModal && selectedInstanceForMemory && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Memory Settings - {selectedInstanceForMemory?.name}
                </h3>
                <button
                  onClick={() => setShowMemoryModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>

              {/* Current Memory Info */}
              <div className="mb-6">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Current Memory:</strong> {(selectedInstanceForMemory.memory_mb / 1024).toFixed(1)} GB ({selectedInstanceForMemory.memory_mb} MB)<br/>
                    <strong>VM Status:</strong> <span className={selectedInstanceForMemory.status === 'running' ? 'text-green-600' : 'text-yellow-600'}>{selectedInstanceForMemory.status}</span>
                  </p>
                  {selectedInstanceForMemory.status === 'running' && (
                    <p className="text-xs text-blue-600 mt-2">
                      Note: Memory changes on running VMs may require a restart to take full effect.
                    </p>
                  )}
                </div>
              </div>

              {/* Memory Adjustment */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Memory (GB)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="512"
                    max="65536"
                    step="512"
                    value={newMemoryMB}
                    onChange={(e) => setNewMemoryMB(Number(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="w-24 text-right">
                    <span className="text-lg font-semibold text-gray-900">{(newMemoryMB / 1024).toFixed(1)} GB</span>
                  </div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>512 MB</span>
                  <span>64 GB</span>
                </div>
              </div>

              {/* Quick Select Buttons */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Select
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {[1024, 2048, 4096, 8192, 16384, 24576, 32768, 49152, 65536].map((mb) => (
                    <button
                      key={mb}
                      onClick={() => setNewMemoryMB(mb)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        newMemoryMB === mb
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {mb >= 1024 ? `${(mb / 1024).toFixed(0)} GB` : `${mb} MB`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Change Summary - only show when no result yet */}
              {!memoryUpdateResult && newMemoryMB !== selectedInstanceForMemory.memory_mb && (
                <div className="mb-6">
                  <div className={`rounded-lg p-4 border ${
                    newMemoryMB > selectedInstanceForMemory.memory_mb
                      ? 'bg-green-50 border-green-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}>
                    <p className={`text-sm ${
                      newMemoryMB > selectedInstanceForMemory.memory_mb ? 'text-green-800' : 'text-yellow-800'
                    }`}>
                      <strong>Change:</strong> {(selectedInstanceForMemory.memory_mb / 1024).toFixed(1)} GB &rarr; {(newMemoryMB / 1024).toFixed(1)} GB
                      <span className="ml-2">
                        ({newMemoryMB > selectedInstanceForMemory.memory_mb ? '+' : ''}
                        {((newMemoryMB - selectedInstanceForMemory.memory_mb) / 1024).toFixed(1)} GB)
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {/* Result Status Display */}
              {memoryUpdateResult && (
                <div className="mb-6">
                  <div className={`rounded-lg p-4 border ${
                    memoryUpdateResult.success
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-start gap-3">
                      {memoryUpdateResult.success ? (
                        <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      <div className="flex-1">
                        <p className={`font-semibold ${memoryUpdateResult.success ? 'text-green-800' : 'text-red-800'}`}>
                          {memoryUpdateResult.success ? 'Success!' : 'Error'}
                        </p>
                        <p className={`text-sm mt-1 ${memoryUpdateResult.success ? 'text-green-700' : 'text-red-700'}`}>
                          {memoryUpdateResult.message}
                        </p>
                        {memoryUpdateResult.requiresRestart && (
                          <div className="mt-3 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
                            <p className="text-sm text-yellow-800 font-medium">
                              VM Restart Required
                            </p>
                            <p className="text-xs text-yellow-700 mt-1">
                              The VM needs to be restarted for the memory changes to take effect.
                            </p>
                            <button
                              onClick={handleRestartAfterMemoryChange}
                              disabled={restartingAfterMemory}
                              className={`mt-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                restartingAfterMemory
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-yellow-600 text-white hover:bg-yellow-700'
                              }`}
                            >
                              {restartingAfterMemory ? (
                                <span className="flex items-center gap-2">
                                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                  </svg>
                                  Restarting VM...
                                </span>
                              ) : (
                                'Restart VM Now'
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowMemoryModal(false);
                    setMemoryUpdateResult(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={memoryChanging || restartingAfterMemory}
                >
                  {memoryUpdateResult?.success ? 'Close' : 'Cancel'}
                </button>
                {!memoryUpdateResult?.success && (
                  <button
                    onClick={handleChangeMemory}
                    disabled={memoryChanging || newMemoryMB === selectedInstanceForMemory.memory_mb}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      memoryChanging || newMemoryMB === selectedInstanceForMemory.memory_mb
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {memoryChanging ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Updating Memory...
                      </span>
                    ) : (
                      'Apply Changes'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Networking Management Modal */}
      {showNetworkingModal && selectedInstanceForNetworking && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  🌐 Networking Management - {selectedInstanceForNetworking?.name}
                </h3>
                <button
                  onClick={() => setShowNetworkingModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* IPv6 Section */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">IPv6 Configuration</h4>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">IPv6 Address</p>
                      <p className="text-base font-mono text-gray-900">
                        {networkingInfo?.ipv6_address || selectedInstanceForNetworking?.ipv6_address || 'Not assigned (DHCPv6)'}
                      </p>
                      {!networkingInfo?.ipv6_address && !selectedInstanceForNetworking?.ipv6_address && (
                        <p className="text-xs text-gray-500 mt-1">
                          IPv6 is assigned via Proxmox DHCPv6. Check VM console for actual address.
                        </p>
                      )}
                    </div>
                    <div>
                      {!networkingInfo?.ipv6_address && !selectedInstanceForNetworking?.ipv6_address ? (
                        <button
                          onClick={() => handleAttachIPv6(selectedInstanceForNetworking.id)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Attach IPv6
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDetachIPv6(selectedInstanceForNetworking.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          Detach IPv6
                        </button>
                      )}
                    </div>
                  </div>
                  {(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname) ? (
                    <div className="pt-3 border-t border-gray-300">
                      <p className="text-sm text-gray-600 mb-1">
                        {(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname)?.includes('.') 
                          ? '🌐 Route53 DNS Domain' 
                          : '🌐 DNS Domain / Hostname'}
                      </p>
                      <p className={`text-base font-mono font-semibold ${(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname)?.includes('.') ? 'text-blue-600' : 'text-blue-600'}`}>
                        {connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname)?.includes('.') 
                          ? '✅ Route53 DNS record mapped. Use this domain name to connect instead of the IPv6 address'
                          : 'DNS domain configured. Use this hostname to connect.'}
                      </p>
                    </div>
                  ) : (
                    <div className="pt-3 border-t border-gray-300">
                      <p className="text-sm text-gray-600 mb-1">🌐 Route53 DNS Domain</p>
                      <p className="text-base text-gray-500">Not configured</p>
                      <p className="text-xs text-gray-500 mt-1">
                        No DNS domain mapped yet. DNS record will be created automatically when IPv6 is attached.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Network Info */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>IPv4:</strong> {networkingInfo?.ipv4_address || selectedInstanceForNetworking?.ipv4_address || 'Pending'}<br/>
                  <strong>IPv6:</strong> {networkingInfo?.ipv6_address || selectedInstanceForNetworking?.ipv6_address || 'Not assigned (DHCPv6)'}<br/>
                  {(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname) ? (
                    <>
                      <strong>{(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname)?.includes('.') ? '🌐 Route53 DNS Domain:' : 'Hostname:'}</strong>{' '}
                      <span className={`font-mono font-semibold ${(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname)?.includes('.') ? 'text-blue-600' : ''}`}>
                        {connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname}
                      </span>
                    </>
                  ) : (
                    <>
                      <strong>🌐 Route53 DNS Domain:</strong> <span className="text-gray-500">Not configured</span>
                    </>
                  )}
                </p>
              </div>

              {/* Connection Info & Credentials Section */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">🔐 Connection & Credentials</h4>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
                  {/* IP Addresses Display */}
                  <div className="bg-white rounded p-3 border border-gray-300">
                    <p className="text-sm font-medium text-gray-700 mb-2">IP Addresses:</p>
                    <div className="space-y-1">
                      {(connectionInfo?.ipv4_address || networkingInfo?.ipv4_address || selectedInstanceForNetworking?.ipv4_address) ? (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">IPv4:</span>
                          <code className="text-xs text-gray-800 font-mono">
                            {connectionInfo?.ipv4_address || networkingInfo?.ipv4_address || selectedInstanceForNetworking?.ipv4_address}
                          </code>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">IPv4:</span>
                          <span className="text-xs text-gray-400">Not assigned</span>
                        </div>
                      )}
                      {(connectionInfo?.ipv6_address || networkingInfo?.ipv6_address || selectedInstanceForNetworking?.ipv6_address) ? (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">IPv6:</span>
                          <code className="text-xs text-gray-800 font-mono break-all">
                            {connectionInfo?.ipv6_address || networkingInfo?.ipv6_address || selectedInstanceForNetworking?.ipv6_address}
                          </code>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">IPv6:</span>
                          <span className="text-xs text-gray-400">Not assigned (DHCPv6)</span>
                        </div>
                      )}
                      {(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname) ? (
                        <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                          <span className="text-xs text-gray-600">
                            {(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname)?.includes('.') ? '🌐 Route53 DNS:' : 'Hostname:'}
                          </span>
                          <code className={`text-xs font-mono font-semibold ${(connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname)?.includes('.') ? 'text-blue-600' : 'text-gray-800'}`}>
                            {connectionInfo?.hostname || networkingInfo?.hostname || selectedInstanceForNetworking?.hostname}
                          </code>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                          <span className="text-xs text-gray-500">🌐 Route53 DNS:</span>
                          <span className="text-xs text-gray-400">Not configured</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* SSH Connection Commands */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">SSH Connection:</p>
                    <div className="space-y-2">
                        {(connectionInfo?.ssh_commands?.by_ipv4 || (connectionInfo?.ipv4_address || networkingInfo?.ipv4_address || selectedInstanceForNetworking?.ipv4_address)) && (
                          <div className="bg-white rounded p-2 border border-gray-300">
                            <code className="text-xs text-gray-800 break-all">
                              {connectionInfo?.ssh_commands?.by_ipv4 || `ssh ${connectionInfo?.username || getDefaultUsernameForOS(selectedInstanceForNetworking?.os_template)}@${connectionInfo?.ipv4_address || networkingInfo?.ipv4_address || selectedInstanceForNetworking?.ipv4_address}`}
                            </code>
                            <button
                              onClick={() => {
                                const sshUser = connectionInfo?.username || getDefaultUsernameForOS(selectedInstanceForNetworking?.os_template);
                                const cmd = connectionInfo?.ssh_commands?.by_ipv4 || `ssh ${sshUser}@${connectionInfo?.ipv4_address || networkingInfo?.ipv4_address || selectedInstanceForNetworking?.ipv4_address}`;
                                navigator.clipboard.writeText(cmd);
                                setSuccess('SSH command copied to clipboard!');
                              }}
                              className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                            >
                              Copy
                            </button>
                          </div>
                        )}
                        {(connectionInfo?.ssh_commands?.by_ipv6 || (connectionInfo?.ipv6_address || networkingInfo?.ipv6_address || selectedInstanceForNetworking?.ipv6_address)) && (
                          <div className="bg-white rounded p-2 border border-gray-300">
                            <code className="text-xs text-gray-800 break-all">
                              {connectionInfo?.ssh_commands?.by_ipv6 || `ssh -6 ${connectionInfo?.username || getDefaultUsernameForOS(selectedInstanceForNetworking?.os_template)}@${connectionInfo?.ipv6_address || networkingInfo?.ipv6_address || selectedInstanceForNetworking?.ipv6_address}`}
                            </code>
                            <button
                              onClick={() => {
                                const sshUser = connectionInfo?.username || getDefaultUsernameForOS(selectedInstanceForNetworking?.os_template);
                                const cmd = connectionInfo?.ssh_commands?.by_ipv6 || `ssh -6 ${sshUser}@${connectionInfo?.ipv6_address || networkingInfo?.ipv6_address || selectedInstanceForNetworking?.ipv6_address}`;
                                navigator.clipboard.writeText(cmd);
                                setSuccess('SSH command copied to clipboard!');
                              }}
                              className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                            >
                              Copy
                            </button>
                          </div>
                        )}
                        {connectionInfo?.ssh_commands?.by_dns && (
                          <div className="bg-white rounded p-2 border border-gray-300">
                            <code className="text-xs text-gray-800 break-all">
                              {connectionInfo.ssh_commands.by_dns}
                            </code>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(connectionInfo.ssh_commands.by_dns);
                                setSuccess('SSH command copied to clipboard!');
                              }}
                              className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                            >
                              Copy
                            </button>
                          </div>
                        )}
                      {(!connectionInfo?.ssh_commands?.by_ipv4 && !connectionInfo?.ssh_commands?.by_ipv6 && !connectionInfo?.ssh_commands?.by_dns && !(connectionInfo?.ipv4_address || networkingInfo?.ipv4_address || selectedInstanceForNetworking?.ipv4_address)) && (
                        <div className="text-xs text-gray-500 italic">
                          No SSH connection available. IP addresses are being assigned...
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Username: <strong>{connectionInfo?.username || getDefaultUsernameForOS(selectedInstanceForNetworking?.os_template)}</strong>
                    </p>
                  </div>

                  {/* Password Management */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-medium text-gray-700">Password</p>
                      {!showPasswordReset ? (
                        <button
                          onClick={() => setShowPasswordReset(true)}
                          className="px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
                        >
                          Reset Password
                        </button>
                      ) : (
                        <div className="flex space-x-2">
                          <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <button
                            onClick={() => handleResetPassword(selectedInstanceForNetworking.id)}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setShowPasswordReset(false);
                              setNewPassword('');
                            }}
                            className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* SSH Keys Management */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-medium text-gray-700">
                        SSH Keys ({connectionInfo?.ssh_keys_count || 0})
                      </p>
                      {!showSSHKeyAdd ? (
                        <button
                          onClick={() => setShowSSHKeyAdd(true)}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          Add Key
                        </button>
                      ) : (
                        <div className="flex-1 ml-2 space-y-2">
                          <textarea
                            value={newSSHKey}
                            onChange={(e) => setNewSSHKey(e.target.value)}
                            placeholder="Paste SSH public key (ssh-rsa ... or ssh-ed25519 ...)"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            rows={2}
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleAddSSHKey(selectedInstanceForNetworking.id)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                            >
                              Add
                            </button>
                            <button
                              onClick={() => {
                                setShowSSHKeyAdd(false);
                                setNewSSHKey('');
                              }}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* List existing SSH keys */}
                    {connectionInfo?.ssh_keys && connectionInfo.ssh_keys.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {connectionInfo.ssh_keys.map((key: string, idx: number) => (
                          <div key={idx} className="bg-white rounded p-2 border border-gray-300 flex justify-between items-start">
                            <code className="text-xs text-gray-700 break-all flex-1 mr-2">
                              {key.substring(0, 60)}...
                            </code>
                            <button
                              onClick={() => handleRemoveSSHKey(selectedInstanceForNetworking.id, key)}
                              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Provisioning Console Modal */}
      {showConsoleModal && selectedInstanceForConsole && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.75)' }}>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl shadow-2xl max-w-5xl w-full border-2 border-gray-700">
              {/* Header */}
              <div className="bg-gray-800 rounded-t-xl px-6 py-4 border-b-2 border-gray-700">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                      <span className="text-3xl">🖥️</span>
                      <span>Provisioning Console - {selectedInstanceForConsole.name}</span>
                    </h3>
                    <p className="text-gray-400 text-sm mt-1">
                      Container: {selectedInstanceForConsole.container_name} | Status: 
                      <span className={`ml-1 font-semibold ${
                        selectedInstanceForConsole.status === 'creating' ? 'text-yellow-400' :
                        selectedInstanceForConsole.status === 'running' ? 'text-green-400' :
                        selectedInstanceForConsole.status === 'terminated' ? 'text-red-400' :
                        'text-gray-400'
                      }`}>
                        {selectedInstanceForConsole.status}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => fetchProvisioningLogs(selectedInstanceForConsole.id)}
                      disabled={loadingLogs}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 flex items-center gap-2"
                    >
                      <span>🔄</span>
                      <span>Refresh</span>
                    </button>
                    <button
                      onClick={handleCloseConsole}
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 font-semibold"
                    >
                      ✕ Close
                    </button>
                  </div>
                </div>
              </div>

              {/* Console Output */}
              <div className="p-6">
                <div 
                  data-console-output
                  className="bg-black rounded-lg border-2 border-gray-700 p-4 font-mono text-sm overflow-auto" 
                  style={{ maxHeight: '600px', minHeight: '400px' }}
                  ref={(el) => {
                    // Auto-scroll to bottom when component updates
                    if (el && provisioningLogs.length > 0) {
                      setTimeout(() => {
                        el.scrollTop = el.scrollHeight;
                      }, 50);
                    }
                  }}
                >
                  {loadingLogs && provisioningLogs.length === 0 ? (
                    <div className="text-gray-400 flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Loading logs...</span>
                    </div>
                  ) : provisioningLogs.length === 0 ? (
                    <div className="text-gray-500 italic">No logs available yet. Provisioning may not have started.</div>
                  ) : (
                    <div className="space-y-1">
                      {provisioningLogs.map((log: any, idx: number) => {
                        const logTime = new Date(log.timestamp).toLocaleTimeString();
                        const levelColors = {
                          info: 'text-blue-400',
                          success: 'text-green-400',
                          warning: 'text-yellow-400',
                          error: 'text-red-400'
                        };
                        
                        return (
                          <div key={idx} className={`${levelColors[log.level as keyof typeof levelColors] || 'text-gray-300'}`}>
                            <span className="text-gray-500 text-xs mr-2">[{logTime}]</span>
                            <span className={`font-semibold mr-2 ${
                              log.level === 'error' ? 'text-red-500' :
                              log.level === 'warning' ? 'text-yellow-500' :
                              log.level === 'success' ? 'text-green-500' :
                              'text-blue-500'
                            }`}>
                              [{log.level.toUpperCase()}]
                            </span>
                            <span>{log.message}</span>
                            {log.details && (
                              <div className="text-gray-400 text-xs ml-8 mt-1">
                                {typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {selectedInstanceForConsole.status === 'creating' && (
                        <div className="text-yellow-400 mt-4 flex items-center gap-2">
                          <div className="animate-pulse">●</div>
                          <span className="italic">Provisioning in progress...</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Status Summary */}
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                    <div className="text-xs text-gray-400 mb-1">Total Logs</div>
                    <div className="text-xl font-bold text-white">{provisioningLogs.length}</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                    <div className="text-xs text-gray-400 mb-1">Errors</div>
                    <div className="text-xl font-bold text-red-400">
                      {provisioningLogs.filter((l: any) => l.level === 'error').length}
                    </div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                    <div className="text-xs text-gray-400 mb-1">Success</div>
                    <div className="text-xl font-bold text-green-400">
                      {provisioningLogs.filter((l: any) => l.level === 'success').length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Display Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 border-2 border-orange-200">
              <div className="text-center mb-6">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-orange-100 mb-4">
                  <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  🔑 VPS Default Password
                </h3>
                <p className="text-blue-600 font-semibold text-sm mb-1">
                  Use this password to connect via SSH
                </p>
                <p className="text-gray-600 text-sm">
                  Default password: <strong>TempPassw0rd!A</strong> (for all VPS instances)
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default VPS Password:
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={displayedPassword}
                    id="password-display"
                    className="flex-1 px-4 py-3 border-2 border-orange-300 rounded-lg bg-orange-50 font-mono text-lg font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(displayedPassword);
                        setPasswordCopied(true);
                        setTimeout(() => setPasswordCopied(false), 3000);
                      } catch (err) {
                        // Fallback for older browsers
                        const input = document.getElementById('password-display') as HTMLInputElement;
                        input.select();
                        document.execCommand('copy');
                        setPasswordCopied(true);
                        setTimeout(() => setPasswordCopied(false), 3000);
                      }
                    }}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      passwordCopied
                        ? 'bg-green-500 text-white'
                        : 'bg-orange-600 text-white hover:bg-orange-700'
                    }`}
                  >
                    {passwordCopied ? (
                      <>
                        <svg className="h-5 w-5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg className="h-5 w-5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>
                {passwordCopied && (
                  <p className="mt-2 text-sm text-green-600 font-medium">
                    ✅ Password copied to clipboard!
                  </p>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-yellow-800 mb-1">Important Security Notice:</p>
                    <ul className="text-xs text-yellow-700 space-y-1 list-disc list-inside">
                      <li>Save this password in a secure password manager</li>
                      <li>Do not share this password with anyone</li>
                      <li>You will need this password to SSH into your VPS</li>
                      <li>This password cannot be retrieved if lost</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setDisplayedPassword('');
                    setPasswordCopied(false);
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
                >
                  I've Saved It
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Provision New VPS Modal */}
      {showProvisionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  ➕ Provision New VPS
                </h3>
                <button
                  onClick={() => setShowProvisionModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleProvisionVPS} className="space-y-6">
                {/* Customer Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={provisionForm.customer_id}
                    onChange={(e) => setProvisionForm({ ...provisionForm, customer_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={loadingUsers}
                  >
                    <option value="">{loadingUsers ? 'Loading customers...' : 'Select a customer'}</option>
                    {!loadingUsers && allUsers.length === 0 && (
                      <option value="" disabled>No customers found. Please check the User Management tab.</option>
                    )}
                    {!loadingUsers && allUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.email} - {user.first_name || ''} {user.last_name || ''} {user.phone ? `(${user.phone})` : ''}
                      </option>
                    ))}
                  </select>
                  {!loadingUsers && allUsers.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">{allUsers.length} customer(s) available</p>
                  )}
                </div>

                {/* Proxmox Host Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Host Server <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={provisionForm.proxmox_host_id}
                    onChange={(e) => setProvisionForm({ ...provisionForm, proxmox_host_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={loadingHosts}
                  >
                    <option value="">{loadingHosts ? 'Loading hosts...' : 'Select a host server'}</option>
                    {!loadingHosts && proxmoxHosts.length === 0 && (
                      <option value="" disabled>No active hosts found. Please add a Proxmox host first.</option>
                    )}
                    {!loadingHosts && proxmoxHosts.map((host) => {
                      const capacityUsed = host.current_vms || 0;
                      const capacityMax = host.max_vms || 100;
                      const capacityPercent = Math.round((capacityUsed / capacityMax) * 100);
                      const isFull = capacityUsed >= capacityMax;
                      const isInternal = host.is_internal_only === true;
                      return (
                        <option
                          key={host.id}
                          value={host.id}
                          disabled={isFull}
                        >
                          {host.name} - {host.datacenter_location || 'Unknown Location'}
                          ({capacityUsed}/{capacityMax} VMs - {capacityPercent}% used)
                          {host.is_default ? ' ⭐ Default' : ''}
                          {isInternal ? ' ⚠️ INTERNAL ONLY — not for customers' : ''}
                          {isFull ? ' ❌ Full' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {!loadingHosts && proxmoxHosts.length > 0 && (() => {
                    const selected = proxmoxHosts.find(h => h.id.toString() === provisionForm.proxmox_host_id);
                    return (
                      <>
                        <p className="mt-1 text-xs text-gray-500">
                          {proxmoxHosts.length} host(s) available |
                          {selected?.name && (
                            <span className="text-blue-600 ml-1">
                              Selected: {selected.hostname}
                            </span>
                          )}
                        </p>
                        {selected?.is_internal_only && (
                          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                            ⚠️ You've selected an <strong>internal-only</strong> host. Use this
                            for our own internal projects only — never assign a paying customer
                            VPS here. Customer checkout flows never route to this host.
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Plan Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    VPS Plan <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={provisionForm.plan_id}
                    onChange={(e) => setProvisionForm({ ...provisionForm, plan_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={loading && vpsPlans.length === 0}
                  >
                    <option value="">{loading && vpsPlans.length === 0 ? 'Loading plans...' : 'Select a plan'}</option>
                    {!loading && vpsPlans.length === 0 && (
                      <option value="" disabled>No active plans found. Please create a plan first.</option>
                    )}
                    {vpsPlans.filter(p => p.is_active).map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - ${plan.monthly_price}/mo ({plan.cpu_cores} CPU, {plan.memory_mb}MB RAM, {plan.disk_gb}GB Disk)
                      </option>
                    ))}
                  </select>
                  {vpsPlans.filter(p => p.is_active).length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">{vpsPlans.filter(p => p.is_active).length} active plan(s) available</p>
                  )}
                </div>

                {/* VPS Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    VPS Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={provisionForm.name}
                    onChange={(e) => setProvisionForm({ ...provisionForm, name: e.target.value })}
                    placeholder="my-server"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* OS Template - Using new OSCategoryCards component */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Operating System <span className="text-red-500">*</span>
                  </label>
                  {provisionForm.proxmox_host_id ? (
                    <OSCategoryCards
                      selectedTemplateId={provisionForm.template_id || null}
                      onSelectTemplate={(templateId: string, template: OSTemplate) => {
                        // Get the correct username for this OS template
                        const sshUsername = template.default_username || getDefaultUsernameForOS(template.family);
                        setProvisionForm({
                          ...provisionForm,
                          template_id: templateId,
                          os_template: `${template.family}:${template.version}`,
                          sshUsername: sshUsername
                        });
                        setSelectedOSTemplate(template);
                      }}
                      hostId={parseInt(provisionForm.proxmox_host_id)}
                      minCpu={(() => {
                        const plan = vpsPlans.find(p => p.id === provisionForm.plan_id);
                        return plan?.cpu_cores;
                      })()}
                      minRamMb={(() => {
                        const plan = vpsPlans.find(p => p.id === provisionForm.plan_id);
                        return plan?.memory_mb;
                      })()}
                      minDiskGb={(() => {
                        const plan = vpsPlans.find(p => p.id === provisionForm.plan_id);
                        return plan?.disk_gb;
                      })()}
                    />
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
                      <p>Please select a Proxmox Host first to see available OS templates.</p>
                    </div>
                  )}
                  {selectedOSTemplate && (
                    <p className="mt-2 text-sm text-green-600">
                      Selected: {selectedOSTemplate.display_name}
                      {selectedOSTemplate.code_name && ` (${selectedOSTemplate.code_name})`}
                    </p>
                  )}
                </div>

                {/* SSH Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SSH Username
                  </label>
                  <input
                    type="text"
                    value={provisionForm.sshUsername}
                    onChange={(e) => setProvisionForm({ ...provisionForm, sshUsername: e.target.value })}
                    placeholder="ubuntu"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Will be created if it does not exist in the image</p>
                </div>

                {/* Initial Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Initial Password <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="password"
                    value={provisionForm.initialPassword}
                    onChange={(e) => setProvisionForm({ ...provisionForm, initialPassword: e.target.value })}
                    placeholder="Leave empty to auto-generate a secure password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {provisionForm.initialPassword 
                      ? '✅ Your password will be used for SSH access' 
                      : '⚠️ A secure random password will be generated and displayed after creation. Save it immediately!'}
                  </p>
                </div>

                {/* SSH Public Keys */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SSH Public Keys (Optional)
                  </label>
                  <div className="space-y-2">
                    <div className="flex space-x-2">
                      <textarea
                        value={provisionSSHKey}
                        onChange={(e) => setProvisionSSHKey(e.target.value)}
                        placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAAB... or ssh-ed25519 AAAA..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                      />
                      <button
                        type="button"
                        onClick={handleAddProvisionSSHKey}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Add
                      </button>
                    </div>
                    {provisionForm.sshPublicKeys.length > 0 && (
                      <div className="space-y-1">
                        {provisionForm.sshPublicKeys.map((key, idx) => (
                          <div key={idx} className="bg-gray-50 rounded p-2 border border-gray-300 flex justify-between items-start">
                            <code className="text-xs text-gray-700 break-all flex-1 mr-2">
                              {key.substring(0, 60)}...
                            </code>
                            <button
                              type="button"
                              onClick={() => handleRemoveProvisionSSHKey(idx)}
                              className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Add SSH public keys to enable key-based authentication</p>
                </div>

                {/* Submit Buttons */}
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium"
                  >
                    Provision VPS
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowProvisionModal(false);
                      setProvisionForm({
                        customer_id: '',
                        plan_id: '',
                        name: '',
                        os_template: 'ubuntu:22.04',
                        template_id: '',
                        sshUsername: 'ubuntu',
                        initialPassword: '',
                        sshPublicKeys: [],
                        proxmox_host_id: ''
                      });
                      setSelectedOSTemplate(null);
                      setProvisionSSHKey('');
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Proxmox Hosts Tab */}
      {activeTab === 'proxmox-hosts' && <ProxmoxHostManagement />}

      {/* Proxmox Host Firewall Tab */}
      {activeTab === 'host-firewall' && <ProxmoxHostFirewallManagement />}

      {/* VPS Firewall Tab */}
      {activeTab === 'firewall' && <VPSFirewallManagement />}

      {/* IPv4 Pool Tab */}
      {activeTab === 'ipv4-pool' && <IPv4PoolManagement />}

      {/* Backups Tab */}
      {activeTab === 'backups' && <VPSBackupManagement />}

      {/* OS Templates Tab */}
      {activeTab === 'os-templates' && <OSTemplateManagement />}

      {/* Image-related modals removed - libvirt uses cloud images automatically */}
    </div>
  );
};

export default VPSManagement;